/**
 * `model-categorize` - a call that files the chosen item list: production's
 * category follow-up, or a split pipeline's second call.
 *
 * Options: `in`, `items` and `categories` always, `company` to tell the model
 * the register's name and industry, `ocr-text` and `image` to show it the
 * receipt the lines came from (production's follow-up is text only and has
 * neither), `described` to offer the list as `describedCategories` does;
 * `ask`, `receipt-category` and/or `item-categories`; `format: 'compact'` for
 * the compact answer (`compactCategorySchema`), which has no other receipt
 * categories, confidences or alternatives. Needs
 * `resolved:items`: it files the list `resolve` chose, so its item categories
 * file that list.
 */

import { describedCategories } from '../../categories.js';
import { filingFromLines, linesCategoryGrammar, linesCategorySystemPrompt, shortCategoryNames } from '../../filing-layout.js';
import { lineFiling } from '../../extraction.js';
import { filingAnswerKeys, filingFromCompact, linesToCategorize } from '../../providers/categories.js';
import { buildCategorizePrompt } from '../prompt/compose.js';
import type { Candidate, FieldKey, Key, Step } from '../types.js';
import { currentReading, summarize, transportOf } from './support.js';

export interface ModelCategorizeOptions {
  in: ('items' | 'company' | 'categories' | 'described' | 'ocr-text' | 'image')[];
  ask: ('receipt-category' | 'item-categories')[];
  /** `compact`: the compact answer; absent, the full one. */
  format?: 'compact';
}

/**
 * A filing answer (`CATEGORY_JSON_SCHEMA`) as candidates: the receipt's
 * category, and one per item of the list it was shown, `null` where the
 * answer named none.
 */
export function candidatesFromFiling(
  answer: Record<string, unknown>,
  source: string,
  itemCount: number,
  forItems: string | undefined,
  asks: ModelCategorizeOptions['ask'],
): Partial<Record<FieldKey, Candidate[]>> {
  const out: Partial<Record<FieldKey, Candidate[]>> = {};
  if (asks.includes('receipt-category')) {
    out.receiptCategory = [{ value: answer.receiptCategory ?? null, source }];
    out.otherCategories = [{ value: Array.isArray(answer.otherCategories) ? answer.otherCategories : [], source }];
  }
  if (asks.includes('item-categories') && forItems) {
    const categories: unknown[] = Array.from({ length: itemCount }, () => null);
    for (const entry of Array.isArray(answer.items) ? answer.items : []) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      const { line } = row;
      const index = typeof line === 'number' ? line - 1 : typeof line === 'string' ? Number(line) - 1 : NaN;
      if (Number.isInteger(index) && index >= 0 && index < itemCount) categories[index] = lineFiling(row);
    }
    out.itemCategories = [{ value: categories, source, forItems }];
  }
  return out;
}

export const modelCategorizeStep: Step<ModelCategorizeOptions> = {
  name: 'model-categorize',
  needs: (options) => [
    'transport',
    'categories',
    'resolved:items',
    ...(options.in.includes('ocr-text') ? (['text'] as Key[]) : []),
    ...(options.in.includes('image') ? (['image'] as Key[]) : []),
  ],
  provides: (options) => [
    ...(options.ask.includes('receipt-category') ? ['candidates:receiptCategory', 'candidates:otherCategories'] : []),
    ...(options.ask.includes('item-categories') ? ['candidates:itemCategories'] : []),
  ],
  async run(state, context) {
    const transport = transportOf(context.ports);
    const reading = currentReading(state);
    const lines = linesToCategorize(reading);
    const name = (reading.merchant as Record<string, unknown>).name;
    const merchant = typeof name === 'string' && name.trim() ? name.trim() : null;
    if (lines.length === 0 && !merchant) return { skipped: 'nothing to file' };
    const image = context.options.in.includes('image') && transport.images ? (state.input.image ?? null) : null;
    const described = context.options.in.includes('described') ? describedCategories(state.input.categories) : null;
    const prompt = buildCategorizePrompt({
      merchant,
      lines,
      company: state.company,
      categories: described?.categories ?? state.input.categories,
      notes: described?.notes,
      compact: context.options.format === 'compact',
      useCompany: context.options.in.includes('company'),
      ocrText: context.options.in.includes('ocr-text') ? (state.input.text ?? null) : null,
      seesImage: image !== null,
    });
    const compact = context.options.format === 'compact';
    // The categorizer trained on it answers in numbered lines with short names, under a grammar (`filing-layout.ts`).
    const short = compact && transport.filingLayout === 'lines' ? shortCategoryNames(described?.categories ?? state.input.categories) : null;
    const request = short
      ? { ...prompt, system: linesCategorySystemPrompt(prompt.system), schema: {}, grammar: linesCategoryGrammar([...short.values()], lines.map((line) => line.line)) }
      : prompt;
    const response = await transport.send({ ...request, schemaName: compact ? 'receipt_lines' : 'receipt_categories', image });
    const filing = short
      ? filingFromLines(typeof response.raw.text === 'string' ? response.raw.text : '', short)
      : compact ? filingFromCompact(response.raw) : response.raw;
    const answer = described ? filingAnswerKeys(filing, described.toKey) : filing;
    const itemCount = Array.isArray(reading.items) ? reading.items.length : 0;
    const candidates = candidatesFromFiling(answer, context.id, itemCount, state.resolved.items?.source, context.options.ask);
    return { answer, candidates, tokens: { input: response.inputTokens, output: response.outputTokens }, added: summarize(candidates) };
  },
};
